/*
 * Copyright Deskshare, Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import * as React from "react";
import { cn } from "@/lib/utils";

interface ProgressProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Progress value (0-100) */
  value?: number;
  /** Maximum value (default: 100) */
  max?: number;
  /** Whether to show indeterminate state */
  indeterminate?: boolean;
}

/**
 * Progress bar component
 *
 * A simple progress indicator for showing completion status.
 */
const Progress = React.forwardRef<HTMLDivElement, ProgressProps>(
  (
    { className, value = 0, max = 100, indeterminate = false, ...props },
    ref,
  ) => {
    const percentage = Math.min(100, Math.max(0, (value / max) * 100));

    return (
      <div
        ref={ref}
        role="progressbar"
        aria-valuenow={indeterminate ? undefined : value}
        aria-valuemin={0}
        aria-valuemax={max}
        className={cn(
          "relative h-2 w-full overflow-hidden rounded-full bg-gray-200",
          className,
        )}
        {...props}
      >
        <div
          className={cn(
            "h-full bg-blue-600 transition-all duration-300 ease-in-out",
            indeterminate && "animate-indeterminate-progress",
          )}
          style={{
            width: indeterminate ? "50%" : `${percentage}%`,
          }}
        />
      </div>
    );
  },
);

Progress.displayName = "Progress";

export { Progress };
export type { ProgressProps };
